import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import {
  canStillChange,
  derivePreviewRunView,
  type PreviewRunState,
  type PreviewRunSummaryResponse,
} from "@/lib/previewInsights";
import type {
  PreviewRunEventDetailResponse,
  PreviewRunEventPageResponse,
} from "@/lib/previewPromptCall";
import type {
  PreviewRunPromptProvenanceResponse,
  PreviewRunPromptSourceResponse,
} from "@/lib/previewPromptProvenance";
import {
  PROMPT_CALL_PAGE_LIMIT,
  type PreviewRunPromptCallPageResponse,
} from "@/lib/previewPromptInspector";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";
import { isServerAnsweredFailure, isTerminalQueryError } from "./queryClient";

/**
 * Preview Insights — the run page's one read (#1098).
 *
 * `GET /v1/projects/{projectId}/preview-runs/{previewRunRef}` is the compact
 * envelope: the run header, the producer's status, completeness, runtime
 * identity and an event count. It carries NO event and therefore no prompt
 * body — the service sweeps the response for one and refuses it — so polling
 * it is polling metadata, which is the whole of what the approved design
 * (§10.2) lets the page do on a timer. Event pages and bodies are later
 * leaves' reads and are not fetched here.
 *
 * WHAT THE BROWSER SENDS, exhaustively: the Project id and the opaque
 * locator, both in the path. No query string, no header of its own, no body.
 * Everything the run is ABOUT — candidate, target, revision, environment,
 * Liv and Brain sessions — is resolved server-side off the dispatch record
 * (ADR 0013 N6), and `src/invariants/previewEgress.test.tsx` asserts that on
 * the bytes.
 */
export type {
  PreviewRunEventDetailResponse,
  PreviewRunEventPageResponse,
  PreviewRunPromptCallPageResponse,
  PreviewRunPromptProvenanceResponse,
  PreviewRunPromptSourceResponse,
  PreviewRunSummaryResponse,
};

/**
 * Why the run could not be read. Classified by what came back, not by
 * status alone, because two 503s share a status and the contract keeps them
 * apart on purpose (`#1097`): a timed-out read may well succeed on the next
 * attempt, an unavailable target will keep refusing until an operator acts.
 */
export type RunReadFailure =
  /** 403 — authenticated, not authorized for this Project or this read. */
  | { kind: "denied" }
  /** 401 — the sign-in is gone; `QueryErrorState` owns the recovery. */
  | { kind: "unauthenticated" }
  /**
   * 404 — the ONE generic answer for every run this account cannot read at
   * this address: unknown, another Project's, another environment's, a
   * binding the recorded target does not hold, a candidate or target that
   * moved. The browser cannot tell which and must not claim to (ADR 0013 N2).
   */
  | { kind: "not_found" }
  /** 501 — the endpoint is not built in this environment. Terminal. */
  | { kind: "not_implemented" }
  /** 503 `preview_insights_timeout` — the read ran out of time; the same request may succeed. */
  | { kind: "upstream"; recovery: "retry" }
  /** 502/503/504 — unreachable, not serving Preview Insights, refused, or unvalidatable. */
  | { kind: "upstream"; recovery: "operator"; code: string }
  /** Any other ANSWERED failure, carrying the machine code and the request id only. */
  | { kind: "error"; code: string; requestId: string | undefined }
  /**
   * No response arrived at all: the transport rejected before the Editor
   * Service put a status on anything. Kept apart from every arm above because
   * each of those describes an answer, and a sentence beginning "the Editor
   * Service answered" about a request that never connected is false
   * (#1098 review, finding 5). `isServerAnsweredFailure` is the one place
   * that distinction is drawn for every surface.
   */
  | { kind: "transport" };

export function classifyRunReadFailure(error: unknown): RunReadFailure {
  if (!isServerAnsweredFailure(error)) {
    return { kind: "transport" };
  }
  if (error instanceof ApiError) {
    if (error.isForbidden) {
      return { kind: "denied" };
    }
    if (error.isUnauthenticated) {
      return { kind: "unauthenticated" };
    }
    if (error.status === 404) {
      return { kind: "not_found" };
    }
    if (error.isNotImplemented) {
      return { kind: "not_implemented" };
    }
    if (error.code === "preview_insights_timeout") {
      return { kind: "upstream", recovery: "retry" };
    }
    if (error.status === 502 || error.status === 503 || error.status === 504) {
      return { kind: "upstream", recovery: "operator", code: error.code };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  // `isServerAnsweredFailure` is `instanceof ApiError`, so this is unreachable;
  // spelled out so the classifier is total by construction rather than by
  // control-flow analysis.
  return { kind: "transport" };
}

export function usePreviewRunQuery(
  projectId: string,
  previewRunRef: string,
  {
    enabled = true,
    paused = false,
    pollIntervalMs,
  }: {
    /**
     * False when the session lacks `preview.inspect`: the browser mirrors the
     * server's decision and does not ask a question it knows the answer to —
     * and does not ask it again every few seconds.
     */
    enabled?: boolean;
    /** A clinician-held pause. Affects THIS view only, never the conversation. */
    paused?: boolean;
    /** The re-read cadence while the run can still change. */
    pollIntervalMs: number;
  },
): UseQueryResult<PreviewRunSummaryResponse> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.previewRun(projectId, previewRunRef),
    async queryFn() {
      return unwrap(
        await client.GET(
          "/v1/projects/{projectId}/preview-runs/{previewRunRef}",
          { params: { path: { projectId, previewRunRef } } },
        ),
      );
    },
    enabled: enabled && projectId.length > 0 && previewRunRef.length > 0,
    // NEVER A DURABLE COPY. The envelope is a reading of a run at an instant;
    // a remount re-reads it rather than serving the previous visit's
    // reading, and the page gates on `isFetchedAfterMount` for the same
    // reason `ProjectContentSources` does (`gcTime: 0` alone schedules the
    // eviction; it does not prevent a same-tick remount finding the entry).
    staleTime: 0,
    gcTime: 0,
    // NO AUTOMATIC RETRY. The client's default gives every transient failure
    // two more attempts, which on a polled read means a failed poll can spend
    // three requests and — if the third succeeds — silently resume the timer
    // the failure was supposed to stop (#1098 review, finding 3). One attempt
    // per tick, one per press of the retry control; the surface says what
    // happened after the first answer rather than after the third.
    retry: false,
    // THE POLLING LIFECYCLE, decided from the last answer and nothing else:
    // re-read while the view can still change, stop the moment it cannot,
    // stop on any failure (the retry is a control, not a timer), and stop
    // while the clinician holds the pause. TanStack keeps the default
    // `refetchIntervalInBackground: false`, so a hidden tab polls nothing.
    refetchInterval: (query) => {
      if (paused || query.state.status !== "success" || query.state.error) {
        return false;
      }
      const data = query.state.data;
      return data !== undefined && canStillChange(derivePreviewRunView(data))
        ? pollIntervalMs
        : false;
    },
  });
}

// ── The services read (Reliv Labs) ───────────────────────────────────────────

export type PreviewRunServices = components["schemas"]["PreviewRunServices"];
export type PreviewServiceTurn = components["schemas"]["PreviewServiceTurn"];
export type PreviewServiceCall = components["schemas"]["PreviewServiceCall"];

/**
 * The run's resolved pack features and each chat-path service call per turn.
 * Reads no trace, so it answers whether or not one was captured; every string
 * in a call's `result` is a closed token, never patient text. `live` re-reads
 * it while the run can still change.
 */
export function usePreviewRunServicesQuery(
  projectId: string,
  previewRunRef: string | null,
  { enabled = true, live = false }: { enabled?: boolean; live?: boolean } = {},
): UseQueryResult<PreviewRunServices> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.previewRunServices(projectId, previewRunRef ?? ""),
    async queryFn() {
      return unwrap(
        await client.GET(
          "/v1/projects/{projectId}/preview-runs/{previewRunRef}/services",
          {
            params: { path: { projectId, previewRunRef: previewRunRef ?? "" } },
          },
        ),
      );
    },
    enabled: enabled && projectId.length > 0 && previewRunRef !== null,
    staleTime: 0,
    retry: false,
    refetchInterval: live ? 5_000 : false,
  });
}

// ── The event reads (#1102) ─────────────────────────────────────────────────

export type PreviewRunEventRecord = PreviewRunEventDetailResponse["event"];

/**
 * The page ceiling the service publishes (`limit` ≤ 100) — asked for
 * explicitly, so the number of round trips a run costs is the smallest the
 * contract allows rather than whatever the server's default happens to be.
 */
export const EVENT_PAGE_LIMIT = 100;

/**
 * The producer contract's ceiling on the events of one trace
 * (`x-limits.maxTraceEvents`). The cursor is followed for as long as it
 * ADVANCES — `limit` is an item ceiling, not a promise, and the service
 * spends a byte budget as well, so a page may legitimately be short with more
 * behind it (#1122 review). The read stops when the run says it is exhausted,
 * and otherwise only on a defect: a cursor that fails to advance, or more
 * events served than the producer allows. Both are stated as an incomplete
 * list rather than looped on.
 */
export const MAX_TRACE_EVENTS = 2000;

export type PreviewRunEventsReading = {
  /** The run header as the LAST page read carried it. */
  run: PreviewRunState;
  /**
   * Every compact event the run listed, in the order the pages served them,
   * or null when the header said content is unavailable (which is not an
   * empty run). `complete` is false only when the read stopped on a defect
   * with the server still reporting more: a cursor that did not advance, or
   * more events than the producer contract allows.
   */
  content: {
    events: readonly NonNullable<PreviewRunEventRecord>[];
    complete: boolean;
  } | null;
};

/** The highest integral `sequence` among compact records, or undefined when none carries one. */
export function highestSequenceOf(
  events: readonly NonNullable<PreviewRunEventRecord>[],
): number | undefined {
  let last: number | undefined;
  for (const event of events) {
    const sequence = event["sequence"];
    if (typeof sequence === "number" && Number.isInteger(sequence)) {
      last = last === undefined ? sequence : Math.max(last, sequence);
    }
  }
  return last;
}

/**
 * The run's compact events, ALL of them, as one list (#1102).
 *
 * `GET /v1/projects/{projectId}/preview-runs/{previewRunRef}/events` answers
 * one bounded page at a time, with bodies BY REFERENCE and a cursor; this
 * hook follows `hasMore` and `nextAfterSequence` until the run is exhausted
 * (design §7.4: "Incremental reads use `afterSequence` plus a bounded
 * `limit`"). A re-read — a poll while the run is live — continues from the
 * highest sequence already held rather than starting over: events are
 * append-only (the producer's R4), so nothing already listed can change.
 *
 * WHAT THE BROWSER SENDS: the Project id and the opaque locator in the path,
 * and `limit` plus the page's own cursor in the query. No `kind` filter — the
 * timeline reads several families and drops the rest itself, which costs one
 * request per page instead of one per family per poll — and no event
 * reference anywhere: a list is never asked about a particular event.
 *
 * Lifecycle: the same as the envelope's. Enabled only once the clinician has
 * opened the tab (the run page's shell must make exactly the reads it did
 * before), polled while the caller says the run can still change, one attempt
 * per tick, never a durable copy.
 */
export function usePreviewRunEventsQuery(
  projectId: string,
  previewRunRef: string,
  {
    enabled = true,
    pollIntervalMs,
  }: {
    enabled?: boolean;
    /** The re-read cadence, or false to stop — decided by the caller from the run's state. */
    pollIntervalMs: number | false;
  },
): UseQueryResult<PreviewRunEventsReading> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = queryKeys.previewRunEvents(projectId, previewRunRef);
  return useQuery({
    queryKey,
    async queryFn() {
      const previous =
        queryClient.getQueryData<PreviewRunEventsReading>(queryKey);
      const events: NonNullable<PreviewRunEventRecord>[] = [
        ...(previous?.content?.events ?? []),
      ];
      const seen = new Set(
        events
          .map((event) => event["eventRef"])
          .filter((ref): ref is string => typeof ref === "string"),
      );
      let afterSequence = highestSequenceOf(events);
      let run: PreviewRunState | undefined;
      let complete = true;
      for (;;) {
        const page = unwrap(
          await client.GET(
            "/v1/projects/{projectId}/preview-runs/{previewRunRef}/events",
            {
              params: {
                path: { projectId, previewRunRef },
                query: {
                  limit: EVENT_PAGE_LIMIT,
                  ...(afterSequence === undefined ? {} : { afterSequence }),
                },
              },
            },
          ),
        );
        run = page.run;
        if (page.page === null || page.page === undefined) {
          // Content unavailable, stated by the header. Not an empty list, and
          // nothing held from a previous read is kept beside a header that
          // now says there is nothing to read.
          return { run, content: null };
        }
        for (const event of page.page.events) {
          const ref = event["eventRef"];
          if (typeof ref === "string") {
            if (seen.has(ref)) {
              continue;
            }
            seen.add(ref);
          }
          events.push(event);
        }
        const next = page.page.nextAfterSequence;
        if (!page.page.hasMore || next === null || next === undefined) {
          break;
        }
        // A cursor that does not advance would re-read one page forever, and
        // a run past the producer's event ceiling is not a run this contract
        // describes: stop, and say the list is incomplete.
        if (
          (afterSequence !== undefined && next <= afterSequence) ||
          events.length >= MAX_TRACE_EVENTS
        ) {
          complete = false;
          break;
        }
        afterSequence = next;
      }
      return { run, content: { events, complete } };
    },
    enabled: enabled && projectId.length > 0 && previewRunRef.length > 0,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchInterval: (query) => {
      if (
        pollIntervalMs === false ||
        query.state.status !== "success" ||
        query.state.error
      ) {
        return false;
      }
      return pollIntervalMs;
    },
  });
}

/**
 * ONE event of a run with its bodies inline (#1099/#1102) — the only
 * authorized path to a prompt body (approved design §7.4), and the read the
 * Editor Service audits as "full prompt detail viewed". Prompt Inspector uses
 * it for the first prompt call; Flow & Decisions uses it for a classification's
 * recorded result or rationale.
 *
 * Fired only when a clinician asks and only for a reference that came from
 * the run's own event page. `gcTime: 0` evicts it once nothing shows it; the
 * immutable event is not re-read while mounted.
 *
 * WHAT THE BROWSER SENDS: the Project id, the opaque run locator and the
 * opaque event ref, all in the path. No query, no header of its own, no body.
 */
export function usePreviewRunEventQuery(
  projectId: string,
  previewRunRef: string,
  eventRef: string,
  { enabled, patient = false }: { enabled: boolean; patient?: boolean },
): UseQueryResult<PreviewRunEventDetailResponse> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.previewRunEvent(projectId, previewRunRef, eventRef),
    async queryFn() {
      return unwrap(
        await client.GET(
          "/v1/projects/{projectId}/preview-runs/{previewRunRef}/events/{eventRef}",
          { params: { path: { projectId, previewRunRef, eventRef } } },
        ),
      );
    },
    enabled:
      enabled &&
      projectId.length > 0 &&
      previewRunRef.length > 0 &&
      eventRef.length > 0,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    // `patient` is for bulk readers (the conversation): the upstream Liv
    // answers a burst with 429 (surfaced here as 503), which clears in
    // seconds, so a transient failure is retried slowly instead of shown.
    retry: patient
      ? (count, error) => !isTerminalQueryError(error) && count < 5
      : false,
    retryDelay: (count) => 1500 * (count + 1),
  });
}

/**
 * ONE page of a run's prompt calls, narrowed and counted by the server
 * (#1100).
 *
 * `GET …/preview-runs/{ref}/prompt-calls?turnNumber=&agent=&phase=&callType=
 * &afterSequence=&limit=` — the Prompt Inspector's navigation. The server
 * applies the narrowing, cuts the page and counts every axis over the WHOLE
 * run; the browser renders what comes back and asks for the next page by
 * cursor. Nothing here folds pages together and nothing here filters: a
 * browser that filtered would need the run, which is exactly what it must not
 * hold (approved design §7.4).
 *
 * WHAT THE BROWSER SENDS, exhaustively: the Project id and the opaque run
 * locator in the path, and — in the query — the page's `limit`, its
 * `afterSequence` cursor, and whichever of the four runtime coordinates the
 * clinician narrowed on. No event reference (a list is never asked about a
 * particular call), no identifier of anything the run is about, no body.
 *
 * BODIES BY REFERENCE. The page carries each call's hash, size, visibility
 * class and omission reason and never its text — the service sweeps the
 * response for an inline body and refuses to answer if one survived — so a
 * run with four hundred calls costs four hundred references and zero prompts.
 * `usePreviewRunEventQuery` is the only read that carries one, and it runs
 * only when a clinician asks for a particular call.
 *
 * POLLED while the run can still change, at the caller's cadence, so calls
 * captured after the page was read appear without the clinician asking. The
 * cursor is the trace's own sequence, so a poll that lands mid-list shifts no
 * boundary and moves nothing already on screen.
 */
export function usePromptCallPageQuery(
  projectId: string,
  previewRunRef: string,
  query: {
    afterSequence?: number;
    turnNumber?: number;
    agent?: string;
    phase?: string;
    callType?: string;
  },
  {
    enabled = true,
    pollIntervalMs,
  }: {
    enabled?: boolean;
    /** The re-read cadence, or false to stop — decided by the caller from the run's state. */
    pollIntervalMs: number | false;
  },
): UseQueryResult<PreviewRunPromptCallPageResponse> {
  const client = useApiClient();
  const request = { limit: PROMPT_CALL_PAGE_LIMIT, ...query };
  return useQuery({
    queryKey: queryKeys.previewRunPromptCalls(
      projectId,
      previewRunRef,
      request,
    ),
    async queryFn() {
      return unwrap(
        await client.GET(
          "/v1/projects/{projectId}/preview-runs/{previewRunRef}/prompt-calls",
          { params: { path: { projectId, previewRunRef }, query: request } },
        ),
      );
    },
    enabled: enabled && projectId.length > 0 && previewRunRef.length > 0,
    // Never a durable copy: a page is a reading of a run at an instant, and a
    // remount re-reads it rather than serving the previous visit's.
    staleTime: 0,
    gcTime: 0,
    // One attempt per tick and one per press of a control, for the reason the
    // envelope read states above: a silent retry can resume a timer that a
    // failure was supposed to stop.
    retry: false,
    refetchInterval: (query) => {
      if (
        pollIntervalMs === false ||
        query.state.status !== "success" ||
        query.state.error
      ) {
        return false;
      }
      return pollIntervalMs;
    },
  });
}

/**
 * ONE prompt call's ordered provenance layers (#1101).
 *
 * `GET …/preview-runs/{ref}/prompt-calls/{eventRef}/provenance` — the layer
 * view behind the Prompt Inspector's detail pane. The server finds the
 * `prompt_provenance` event that names this call (the vendored upstream read
 * publishes no filter for it, so the match cannot be made in a browser without
 * downloading the run), republishes it, and resolves each layer's clinical
 * coordinate onto a section of THIS Project.
 *
 * WHAT THE BROWSER SENDS, exhaustively: the Project id, the opaque run locator
 * and the opaque event ref, all in the path. No query, no header of its own,
 * no body — and nothing the run is about.
 *
 * BODIES BY REFERENCE. Each layer carries its hash, size, visibility class and
 * omission reason and never its text, so a fifteen-layer prompt costs fifteen
 * references and zero prompts. `usePreviewRunEventQuery` against the
 * provenance event's own ref is the read that carries the layers' text, and it
 * runs only when a clinician asks for it.
 *
 * NOT POLLED. A prompt call is immutable once captured and so is the account
 * of how its prompt was composed: re-reading it on a timer would spend
 * requests to learn nothing. `staleTime: Infinity` keeps it for as long as the
 * panel shows it, `gcTime: 0` drops it when nothing does.
 */
export function usePromptProvenanceQuery(
  projectId: string,
  previewRunRef: string,
  eventRef: string,
  { enabled }: { enabled: boolean },
): UseQueryResult<PreviewRunPromptProvenanceResponse> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.previewRunPromptProvenance(
      projectId,
      previewRunRef,
      eventRef,
    ),
    async queryFn() {
      return unwrap(
        await client.GET(
          "/v1/projects/{projectId}/preview-runs/{previewRunRef}/prompt-calls/{eventRef}/provenance",
          { params: { path: { projectId, previewRunRef, eventRef } } },
        ),
      );
    },
    enabled:
      enabled &&
      projectId.length > 0 &&
      previewRunRef.length > 0 &&
      eventRef.length > 0,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    // One attempt per press of a control: a silent retry can resume work a
    // failure was supposed to stop, and this read costs a whole-run scan.
    retry: false,
  });
}

/**
 * ONE provenance layer's authoring document, as THIS RUN's immutable candidate
 * froze it (#1101 review, finding 1).
 *
 * `GET …/prompt-calls/{eventRef}/provenance/{index}/source` — the read-only
 * source view. The clinical-configuration section a layer also links to is
 * where the document is authored NOW; these are the bytes the run actually
 * used, hash-verified against the row that committed them.
 *
 * WHAT THE BROWSER SENDS, exhaustively: the Project id, the opaque run
 * locator, the opaque event ref and the layer's POSITION — an integer — all in
 * the path. No query, no header of its own, no body. It cannot name a
 * document, a coordinate or a candidate: the server resolves all three off the
 * run's own correlation row, which is what makes "never another candidate" a
 * property of the request shape rather than of this browser's behaviour.
 *
 * Fired only when a clinician opens a layer's source, and never on mount: it
 * spends an archive read and an unpack. `staleTime: Infinity` because a frozen
 * candidate cannot change; `gcTime: 0` drops it when the view closes.
 */
export function usePromptSourceQuery(
  projectId: string,
  previewRunRef: string,
  eventRef: string,
  index: number | null,
): UseQueryResult<PreviewRunPromptSourceResponse> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.previewRunPromptSource(
      projectId,
      previewRunRef,
      eventRef,
      index ?? -1,
    ),
    async queryFn() {
      return unwrap(
        await client.GET(
          "/v1/projects/{projectId}/preview-runs/{previewRunRef}/prompt-calls/{eventRef}/provenance/{index}/source",
          {
            params: {
              path: { projectId, previewRunRef, eventRef, index: index ?? 0 },
            },
          },
        ),
      );
    },
    enabled:
      index !== null &&
      projectId.length > 0 &&
      previewRunRef.length > 0 &&
      eventRef.length > 0,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
  });
}
